import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Grid,
  LinearProgress,
  Paper,
  Typography,
  Skeleton,
  Chip,
  IconButton,
  useTheme,
  alpha,
} from "@mui/material";
import QueueIcon from "@mui/icons-material/Queue";
import RefreshIcon from "@mui/icons-material/Refresh";
import { GetEtkQueueStatuses } from "../../../services/DashboardService";
import "./Panels.css";

export default function EtkQueuePanel({ startDate, endDate, selectedUser }) {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const max = data.length ? Math.max(...data.map((d) => d.count)) : 0;
  const total = data
    .filter(
      (d) =>
        d.status === "Approved Orders" || d.status === "Not Approved Orders"
    )
    .reduce((sum, item) => sum + item.count, 0);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await GetEtkQueueStatuses(startDate, endDate, selectedUser);
      setData(res || []);
      setLastUpdated(new Date());
    } catch (e) {
      setData([]);
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, selectedUser]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const brand = "#007DD1";

  if (loading) {
    return (
      <Box className="panel-loading">
        <Box display="flex" alignItems="center" gap={1.5} mb={2}>
          <Skeleton variant="circular" width={10} height={10} />
          <Skeleton variant="text" width={160} height={20} />
        </Box>
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} md={6} key={i}>
              <Skeleton
                variant="rectangular"
                height={100}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  return (
    <Box className="modern-panel" sx={{ p: 1.5 }}>
      {/* Header */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={1}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: brand,
            }}
          />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: brand }}>
            ETK Queue Overview
          </Typography>
          <Chip
            label={`${total} Total`}
            icon={<QueueIcon sx={{ fontSize: 16 }} />}
            size="small"
            sx={{
              borderColor: brand,
              color: brand,
              "& .MuiChip-icon": { color: brand },
              fontSize: "0.7rem",
              height: 22,
            }}
          />
        </Box>

        {/* Refresh Button */}
        <Box display="flex" alignItems="center" gap={0.5}>
          {lastUpdated && (
            <Typography variant="caption" sx={{ color: brand, fontSize: "0.7rem" }}>
              {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <IconButton onClick={loadData} disabled={loading} size="small">
            <RefreshIcon
              className={loading ? "spin" : ""}
              sx={{ color: brand, fontSize: 18 }}
            />
          </IconButton>
        </Box>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mb: 1.5, ml: 0.5 }}
      >
        ETK queue sample distribution
      </Typography>

      {/* Grid */}
      <Grid container spacing={2}>
        {data.map((row, index) => {
          const percentage = max ? Math.round((row.count / max) * 100) : 0;
          const sharePercentage = total
            ? Math.round((row.count / total) * 100)
            : 0;

          return (
            <Grid item xs={12} sm={6} lg={4} key={row.status || index}>
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  backgroundColor: alpha(brand, 0.02),
                  "&:hover": {
                    boxShadow: `0 0 0 1px ${alpha(brand, 0.25)}`,
                  },
                  transition: "all 0.2s ease-in-out",
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, fontSize: "0.8rem" }}
                  >
                    {row.status || "Unknown Status"}
                  </Typography>
                  <Chip
                    label={`${sharePercentage}%`}
                    size="small"
                    sx={{
                      backgroundColor: alpha(brand, 0.1),
                      color: brand,
                      fontSize: "0.7rem",
                      height: 20,
                    }}
                  />
                </Box>

                <Box mt={1}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: brand,
                      fontSize: "1.2rem",
                      lineHeight: 1.2,
                    }}
                  >
                    {row.count?.toLocaleString() || 0}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ fontSize: "0.7rem" }}
                  >
                    Samples
                  </Typography>
                </Box>

                <Box mt={1}>
                  <LinearProgress
                    variant="determinate"
                    value={percentage}
                    sx={{
                      height: 6,
                      borderRadius: 4,
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: brand,
                      },
                      backgroundColor: alpha(brand, 0.15),
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.3, fontSize: "0.7rem" }}
                  >
                    {percentage}% of max
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          );
        })}

        {!data?.length && (
          <Grid item xs={12}>
            <Box sx={{ textAlign: "center", py: 3, color: "text.secondary" }}>
              <Typography variant="body1" gutterBottom>
                📋 No ETK Queue Data
              </Typography>
              <Typography variant="caption">
                No ETK queue data available for the selected range.
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
